// FELTNETT-FILTERET — hendelsesloggen som ÉN filtrerbar tidslinje.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// UX-gjennomgang 2026-08-18, funn **F1** og **F5**, rapportens Fase 1:
//
//   «Hendelsesloggen blir the felles feed: chat, stridsmeldinger, ordrer og
//   deteksjoner i samme strøm. Filtrer på type, hastegrad eller enhet. Klikk på
//   et kort → kartet flyr til stedet. Ett blikk gir hele bildet.»
//
// Strømmen var på plass etter Fase 0: ni hendelsestyper med hastegrad, alle
// samlet i `hendelseslogg.ts`. Det som manglet var å kunne SKJÆRE i den.
//
// ── ⚠ FELLA SOM STYRER HELE DESIGNET ────────────────────────────────────────
// Et filter er farlig i et situasjonsbilde. En operatør som har satt «bare
// LOGSTAT» og glemt det, ser en tom logg og leser den som «ingenting skjer» —
// mens en CONTACT står og lyser rett utenfor filteret. Da har filteret gjort
// systemet DÅRLIGERE enn det var uten.
//
// Tre regler følger av det, og de er ufravikelige her:
//   1. `filterSammendrag()` sier ALLTID hvor mange som er skjult. En logg som er
//      filtrert skal aldri kunne se ut som en logg der ingenting skjedde.
//   2. Haste-båndet (`hasteBand`) filtreres IKKE. FLASH og IMMEDIATE slipper
//      gjennom uansett hva operatøren har skrudd på. Fase 0 gjorde hastegrad
//      synlig; et filter som kan skjule den ville reversert det.
//   3. `erAktivt()` finnes for at flata skal kunne MERKE at et filter står på,
//      også når resultatet tilfeldigvis ser normalt ut.
// ⚠ FILNAVNET ER MÅLET, IKKE TILSTANDEN. Fila heter `feltnett` fordi den er
// første steg mot rapportens Feltnett-lag. Men i UI-et heter flata «Tidslinje»,
// og det er med vilje: Feltnett er DEFINERT som ett lag der Feltveksler,
// komposeren, Kontakter og Feltmelding er smeltet sammen — og de er fortsatt egne
// paneler. Å skrive «Feltnett» på skjermen nå ville vært samme feil som
// «Feltmelding / PTT» var: et navn som lover noe koden ikke gjør. Navnet flyttes
// inn i UI-et den dagen Fase 2 og 3 faktisk har slått panelene sammen.
import { PRECEDENCE_RANK, type PrecedenceId } from '@skade/contracts';
import type { Hendelse, HendelseType } from './hendelseslogg.js';

/**
 * Filtergrupper — rapporten ber om «type, hastegrad eller enhet».
 *
 * Typene er buntet, ikke listet én for én. Ni avkryssingsbokser er den samme
 * feilen rapporten beskriver i OSINT-kapittelet («tretti brytere og null
 * følelse»): operatøren skal velge et PERSPEKTIV, ikke administrere et sett.
 */
export type FeltnettLinse = 'alt' | 'strid' | 'ordre' | 'sanitet' | 'logistikk' | 'samband' | 'varsel';

/**
 * Hvilke hendelsestyper hver linse slipper gjennom.
 *
 * ⚠ `status` ligger under «strid» sammen med `rapport`: en statusendring på et
 * case ER stridsmeldingens livsløp, og å skille dem ville betydd at en operatør
 * som følger en CONTACT mister øyeblikket den blir kvittert.
 *
 * ⚠ `autorisasjon` ligger under «ordre». Ildfrigivning, delegert myndighet og
 * EMCON er samme familie som en forflytningsordre: noen har bestemt at noe skal
 * eller ikke skal gjøres, og loggsporet ER kontrollen.
 */
export const LINSE_TYPER: Record<FeltnettLinse, readonly HendelseType[]> = {
  alt: ['rapport', 'ordre', 'status', 'melding', 'perstat', 'logstat', 'rfi', 'varsel', 'autorisasjon'],
  strid: ['rapport', 'status'],
  ordre: ['ordre', 'autorisasjon'],
  sanitet: ['perstat'],
  logistikk: ['logstat'],
  samband: ['melding', 'rfi'],
  varsel: ['varsel'],
};

export const LINSE_ETIKETT: Record<FeltnettLinse, string> = {
  alt: 'Alt',
  strid: 'Strid',
  ordre: 'Ordre',
  sanitet: 'Sanitet',
  logistikk: 'Logistikk',
  samband: 'Samband',
  varsel: 'Varsel',
};

/** Rekkefølge i chip-raden. «Alt» først fordi det er utgangspunktet. */
export const LINSER: readonly FeltnettLinse[] = [
  'alt', 'strid', 'ordre', 'varsel', 'sanitet', 'logistikk', 'samband',
];

/**
 * Søkeord per linse — til kommandopaletten.
 *
 * Ordene er dem operatøren TENKER når hun leter («hvor er sanitetsmeldingene»),
 * ikke navnet vi ga knappen. Ligger her og ikke i `main.ts`, så en ny linse ikke
 * kan legges til på ett sted og glemmes på det andre (§3).
 */
export const LINSE_SOKEORD: Record<FeltnettLinse, string> = {
  alt: 'alle tidslinje kronologisk nullstill feltnett',
  strid: 'stridsmelding rapport salute contact tic sitrep medevac cbrn uxo status case',
  ordre: 'fempunktsordre irfmi forflytning myndighet delegering ildfrigivning emcon autorisasjon',
  sanitet: 'perstat tap saaret wia kia mia triage medevac casualty',
  logistikk: 'logstat forsyning ammo drivstoff vann anmodning resupply klasse',
  samband: 'feltmelding melding chat dm rfi informasjonsbehov spoersmaal svar',
  varsel: 'alarm deteksjon hvt tst noed 112 avvik nyhet sensor',
};

export interface FeltnettFilter {
  linse: FeltnettLinse;
  /**
   * Vis bare hendelser MINST så hastende. Udefinert = ingen grense.
   *
   * ⚠ HENDELSER UTEN HASTEGRAD FALLER UT når grensa er satt, og det er riktig:
   * «vi vet ikke hvor mye dette haster» kan ikke oppfylle et krav om «minst
   * IMMEDIATE». Men det er nettopp derfor `filterSammendrag()` må navngi tallet
   * — ellers forsvinner de stille.
   */
  minHastegrad?: PrecedenceId;
  /** Fritekstsøk i tittel + detalj. Tømmes med tom streng. */
  sok: string;
}

export const TOMT_FILTER: FeltnettFilter = { linse: 'alt', sok: '' };

/** Står det et filter på? Flata MERKER dette, også når resultatet ser normalt ut. */
export function erAktivt(f: FeltnettFilter): boolean {
  return f.linse !== 'alt' || f.minHastegrad !== undefined || f.sok.trim().length > 0;
}

/**
 * Passer én hendelse gjennom filteret?
 *
 * Søket er bevisst enkelt (delstreng, ufølsom for store/små bokstaver). Et
 * regex-søk ville gitt en operatør under tidspress en syntaksfeil å feilsøke.
 */
export function passerer(h: Hendelse, f: FeltnettFilter): boolean {
  if (!LINSE_TYPER[f.linse].includes(h.type)) return false;
  if (f.minHastegrad !== undefined) {
    if (!h.hastegrad) return false;
    if (PRECEDENCE_RANK[h.hastegrad] > PRECEDENCE_RANK[f.minHastegrad]) return false;
  }
  const q = f.sok.trim().toLowerCase();
  if (q) {
    const heystakk = `${h.tittel} ${h.detalj}`.toLowerCase();
    if (!heystakk.includes(q)) return false;
  }
  return true;
}

export function filtrerHendelser(inn: Hendelse[], f: FeltnettFilter): Hendelse[] {
  return inn.filter((h) => passerer(h, f));
}

export interface FilterSammendrag {
  /** Hvor mange som vises. */
  vist: number;
  /** Hvor mange som finnes i alt. */
  totalt: number;
  /** Hvor mange filteret gjemte. */
  skjult: number;
  /**
   * Hvor mange av de SKJULTE som var FLASH eller IMMEDIATE.
   *
   * ⚠ DETTE TALLET ER HELE POENGET. Skjuler filteret noe som haster, skal
   * operatøren se det som en ADVARSEL, ikke som en fotnote. Radene står
   * fortsatt i haste-båndet (som ikke filtreres), men teksten må si det så
   * ingen tror båndet og lista er uenige.
   */
  skjultHastende: number;
  /** Ferdig linje til flata. */
  tekst: string;
  /** Skal linja vises som advarsel? */
  advarsel: boolean;
}

/**
 * Beskriv utvalget.
 *
 * ⚠ SIER ALLTID NOE. Selv med filteret av oppgis totalen, for et tall som bare
 * dukker opp når man filtrerer er et tall ingen har lært å lese.
 */
export function filterSammendrag(inn: Hendelse[], f: FeltnettFilter): FilterSammendrag {
  const vist = filtrerHendelser(inn, f);
  const skjulte = inn.filter((h) => !passerer(h, f));
  const skjultHastende = skjulte.filter(
    (h) => h.hastegrad === 'flash' || h.hastegrad === 'immediate',
  ).length;
  const grunnlag = `Viser ${vist.length} av ${inn.length}`;

  if (!erAktivt(f)) {
    return {
      vist: vist.length, totalt: inn.length, skjult: 0, skjultHastende: 0,
      tekst: `${grunnlag} — ingen filter`, advarsel: false,
    };
  }
  const deler = [`${grunnlag} · filter: ${LINSE_ETIKETT[f.linse]}`];
  if (f.minHastegrad) deler.push(`minst ${f.minHastegrad.toUpperCase()}`);
  if (f.sok.trim()) deler.push(`«${f.sok.trim()}»`);
  let tekst = deler.join(' · ');
  if (skjultHastende > 0) {
    // Ordlyden peker på hvor radene FINNES. «4 skjult» alene etterlater
    // operatøren med et tall og ingen handling.
    tekst += ` — ⚠ ${skjultHastende} hastende skjult, se HASTER øverst`;
  } else if (skjulte.length > 0) {
    tekst += ` · ${skjulte.length} skjult`;
  }
  return {
    vist: vist.length, totalt: inn.length, skjult: skjulte.length,
    skjultHastende, tekst, advarsel: skjultHastende > 0,
  };
}

/**
 * Hvor mange treff hver linse ville gitt — til tallet i chip-en.
 *
 * Finnes fordi en linse uten tall er en gjetning: operatøren skal kunne se at
 * det er fire ting under «Sanitet» UTEN å klikke seg dit og tilbake. Regnes på
 * hastegrad og søk, men IKKE på linsa selv (ellers ville hver chip vist
 * totalen for den linsa som alt er valgt).
 */
export function linseTelling(
  inn: Hendelse[],
  f: FeltnettFilter,
): Record<FeltnettLinse, number> {
  const ut = {} as Record<FeltnettLinse, number>;
  for (const l of LINSER) {
    ut[l] = filtrerHendelser(inn, { ...f, linse: l }).length;
  }
  return ut;
}
